import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {useState} from 'react';
import {Link} from 'react-router-dom';
import {laDateCourte} from '../utils/dates';
import {TopicIcon} from './TopicIcon';
import {
  demandLabel,
  STATUS_COLORS,
  STATUS_LABELS,
  TAG_COLORS,
  TAG_LABELS,
} from '../types/feedback';
import {useOtherFeedbacksByUser} from '../hooks/useFeedback';
import {dashboardPath, feedbackPath} from '../routes';

interface UserHistorySectionProps {
  uid: string;
  excludeId: string;
}

function truncate(text: string, n: number): string {
  if (!text) return '';
  return text.length <= n ? text : text.slice(0, n).trimEnd() + '…';
}

export function UserHistorySection({uid, excludeId}: UserHistorySectionProps) {
    const t = useTraduire();
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  // Lazy-load: only start fetching when first opened; keep data afterwards.
  const {items, loading, error} = useOtherFeedbacksByUser(
    hasOpened ? uid : undefined,
    hasOpened ? excludeId : undefined,
  );

  const handleToggle = () => {
    setOpen((v) => !v);
    if (!hasOpened) setHasOpened(true);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 rounded-lg"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-medium text-gray-900">
            {t(T.autresDemandes)}
          </h3>
          {hasOpened && !loading && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
              {items.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={dashboardPath(uid)}
            onClick={(e) => e.stopPropagation()}
            className="text-sm text-primary hover:text-primary-dark"
          >
            {t(T.voirToutDansLaListe)}
          </Link>
          <svg
            className={`w-5 h-5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
          </svg>
        </div>
      </button>

      {open && (
        <div className="border-t border-gray-100 px-6 pb-6 pt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
              <span className="text-sm">{t(T.chargementHistorique)}</span>
            </div>
          ) : error ? (
            <p className="text-sm text-red-600">{error}</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-500 italic">{t(T.aucuneAutreDemande)}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {items.map((item) => (
                <li key={item.id}>
                  <Link
                    to={feedbackPath(item.id)}
                    className="flex items-start gap-3 py-3 hover:bg-gray-50 -mx-2 px-2 rounded"
                  >
                    <div className="flex-shrink-0 w-28 text-xs text-gray-500">
                      {item.createdAt?.toDate
                        ? laDateCourte(item.createdAt.toDate())
                        : '-'}
                    </div>
                    <div className="flex-shrink-0 flex flex-col gap-1">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          STATUS_COLORS[item.status || 'new']
                        }`}
                      >
                        {t(STATUS_LABELS[item.status || 'new'])}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                        <TopicIcon topic={item.topic} size={13}/>
                        {t(demandLabel(item))}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{truncate(item.message, 200)}</p>
                      {item.tags && item.tags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {item.tags.map((tag) => (
                            <span
                              key={tag}
                              className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full ${TAG_COLORS[tag]}`}
                            >
                              {t(TAG_LABELS[tag])}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-gray-400">
                        {item.platform} · {item.appVersion}
                        {item.hasUnreadUserReply && (
                          <span className="ml-2 inline-flex items-center gap-1 text-red-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500"/> unread reply
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
